import { useRef, useState } from "react";
import { images } from "@img/imagesMap";
import * as excel from "@script/excel";
import LoadingDots from "@components/LoadingDots";
import httpClient from "@/config/axios";
import { useMutation } from "@tanstack/react-query";
import { getGroupIdQuery } from "@/utils/common-util";
import { ManagementDialog } from "@/components/modal/ManagementDialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/toast/CustomToast";

type Props = {
  onClose: () => void;
  isMasterAdmin?: boolean;
  selectedGroupId?: string | null;
  fetchNurseSchema?: (groupId?: string) => void;
};

export default function MgmtExcelUpload2({
  onClose,
  isMasterAdmin,
  selectedGroupId,
  fetchNurseSchema,
}: Props) {
  const [excelFileName, setExcelFileName] = useState<string | undefined>();
  const [isUpload, setIsUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileObj, setFileObj] = useState<File | null>(null);

  const groupId = selectedGroupId ?? null;

  // validate/confirm states
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [validatedRows, setValidatedRows] = useState<
    Record<string, unknown>[] | null
  >(null);
  const [errors, setErrors] = useState<Array<{ row: number; reason: string }>>(
    []
  );
  const [summary, setSummary] = useState<{
    total: number;
    head_nurses: number;
    error_count: number;
  } | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  function clickUploadButton() {
    fileInputRef.current?.click();
  }

  async function handleFileUpload(file: File) {
    if (isSaving || isValidating) return;
    setIsUpload(true);
    if (file.size > 10 * 1024 * 1024) {
      toast.error("파일 크기는 10MB를 초과할 수 없습니다.");
      setIsUpload(false);
      return;
    }
    if (
      !file.name.toLowerCase().endsWith(".xlsx") &&
      !file.name.toLowerCase().endsWith(".xls")
    ) {
      toast.error("지원되지 않는 파일 형식입니다. (.xlsx, .xls만 지원)");
      setIsUpload(false);
      return;
    }
    setExcelFileName(file.name);
    setValidatedRows(null);
    setErrors([]);
    setSummary(null);
    // 파일만 보관, 검증은 별도 버튼에서 수행
    setFileObj(file);
    setIsUpload(false);
  }

  async function runValidate() {
    if (!fileObj) {
      toast.error("파일을 먼저 선택해주세요.");
      return;
    }
    setIsValidating(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const res = await httpClient.post<{
        errors?: Array<{ row: number; reason: string }>;
        rows?: Record<string, unknown>[];
        summary?: {
          total: number;
          head_nurses: number;
          error_count: number;
        };
      }>(getGroupIdQuery("/nurses/upload2-validate", groupId ?? null), fd);

      const errs: Array<{ row: number; reason: string }> = Array.isArray(
        res?.errors
      )
        ? res.errors
        : [];

      setErrors(errs);
      const rows = Array.isArray(res?.rows)
        ? (res.rows as Record<string, unknown>[])
        : [];
      setValidatedRows(rows);
      setSummary(res?.summary ?? null);
      if (errs.length > 0) {
        setShowErrorModal(true);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "검증 실패");
    } finally {
      setIsValidating(false);
    }
  }

  async function runConfirm() {
    if (!validatedRows || errors.length > 0) return;
    setIsSaving(true);
    try {
      const gid = selectedGroupId ?? undefined;

      const res = await httpClient.post<{
        saved?: number;
        updated?: number;
      }>(getGroupIdQuery("/nurses/upload2-confirm", groupId ?? null), {
        rows: validatedRows,
      });

      const saved = res?.saved ?? 0;
      const updated = res?.updated ?? 0;

      toast.success(`저장 완료: 신규 ${saved}건, 업데이트 ${updated}건`);
      // 목록 갱신
      if (fetchNurseSchema) {
        fetchNurseSchema(gid);
      }
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "저장 실패");
    } finally {
      setIsSaving(false);
    }
  }

  // 회원정보 가져와서 엑셀 다운로드
  const { mutate: downloadMembersExcel, isPending: isDownloading } =
    useMutation({
      mutationFn: async () => {
        return await httpClient.get<Blob>(
          getGroupIdQuery("/nurses/export-members", groupId ?? null),
          {
            responseType: "blob",
          }
        );
      },
      onSuccess: (data: Blob) => {
        const url = window.URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `구성원_목록.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      onError: (error) => {
        toast.error(error.message || "다운로드 실패");
      },
    });

  return (
    <>
      <ManagementDialog
        onClose={onClose}
        title="근무자 엑셀 업로드"
        widthClassName="max-w-[760px]"
        isBusy={isSaving || isValidating || isUpload}
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={onClose}
              disabled={isSaving || isValidating || isUpload}
            >
              닫기
            </Button>
            {validatedRows && (
              <Button
                type="button"
                className="rounded-full bg-sig text-white hover:bg-sig/90"
                disabled={
                  isSaving || isValidating || isUpload || errors.length > 0
                }
                onClick={runConfirm}
              >
                {isSaving ? "저장 중..." : "저장"}
              </Button>
            )}
          </>
        }
      >
        <div className="relative flex min-w-0 flex-col">
          <div className="flex min-w-0 flex-col gap-3">
            <div
              className="d-f jc-fe"
              style={{ columnGap: "16px", marginBottom: "16px" }}
            >
              {isMasterAdmin && (
                <button
                  type="button"
                  className="excel_template_download"
                  onClick={() => downloadMembersExcel()}
                  disabled={isDownloading}
                >
                  <img src={images.icon_excel} alt="" />
                  {isDownloading
                    ? "다운로드 중..."
                    : "전체 근무자 정보 가져오기"}
                </button>
              )}
              <button
                type="button"
                className="excel_template_download"
                onClick={excel.template_download2}
              >
                <img src={images.icon_excel} alt="" />
                템플릿 다운로드
              </button>
            </div>

            <div
              className="excel_upload_dragArea"
              style={{ width: "100%" }}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                  handleFileUpload(e.dataTransfer.files[0]);
                  e.dataTransfer.clearData();
                }
              }}
            >
              <div
                className={`excel_upload_fileName max-w-full break-all text-center ${!excelFileName && "excel_upload_fileName--noFile"}`}
              >
                {excelFileName
                  ? excelFileName
                  : "클릭하거나, 파일을 끌어와 업로드해주세요."}
              </div>

              <button
                type="button"
                aria-label="엑셀 파일 선택"
                onClick={clickUploadButton}
                disabled={isSaving || isValidating || isUpload}
              >
                <span className="conf_addRowButton_plusIcon conf_addRowButton_plusIcon--large"></span>
              </button>
              {excelFileName && (
                <button
                  type="button"
                  className="component_button ta-c"
                  style={{ backgroundColor: "#fff" }}
                  onClick={runValidate}
                  disabled={isSaving || isValidating || !fileObj}
                >
                  {isValidating ? "검증 중..." : "검증"}
                </button>
              )}
            </div>
            <input
              type="file"
              className="excel_upload_inputFile"
              ref={fileInputRef}
              accept=".xlsx, .xls"
              onInput={(e) => {
                if (e.currentTarget.files && e.currentTarget.files[0]) {
                  handleFileUpload(e.currentTarget.files[0]);
                }
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            />
            {summary && (
              <div style={{ marginTop: 12, fontSize: 12, color: "#444" }}>
                <div>
                  총 행수: {summary.total} / 수간호사: {summary.head_nurses} /
                  오류: {summary.error_count}
                </div>
              </div>
            )}
          </div>
          {isUpload && (
            <div className="excel_uploading_bg">
              <div>업로드 중입니다</div>
              <LoadingDots />
            </div>
          )}
        </div>
      </ManagementDialog>

      <ManagementDialog
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="검증 오류"
        widthClassName="max-w-[720px]"
        footer={
          <Button
            type="button"
            className="rounded-full bg-sig text-white hover:bg-sig/90"
            onClick={() => setShowErrorModal(false)}
          >
            확인
          </Button>
        }
      >
        <div className="text-left leading-relaxed" style={{ lineHeight: 1.6 }}>
          <div className="comp_table">
            <div className="comp_thead comp_tr comp_tr--validationExcelUpload">
              <div className="comp_td">행번호</div>
              <div className="comp_td">내용</div>
            </div>
            <div className="comp_tbody">
              {errors
                .sort((a, b) => a.row - b.row)
                .map((e) => (
                  <div
                    key={`${e.row}-${e.reason}`}
                    className="comp_tr comp_tr--validationExcelUpload"
                  >
                    <div className="comp_td">{e.row}</div>
                    <div
                      className="comp_td"
                      style={{ justifyContent: "flex-start" }}
                    >
                      {e.reason}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </ManagementDialog>
    </>
  );
}
